import React, { useEffect, useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import API from "../../../../utils/API";
import MesTable from "../../../../components/Table/MesTable";
import editIcon from "../../../../assets/icons/table/edit.svg";
import trashIcon from "../../../../assets/icons/table/trash.svg";
import { signageRequested } from "../../../../store/signage";
import commonMethods from "../../../../utils/common-methods";
import { FormattedMessage } from "react-intl";
import useInfiniteScroll from "../../../../components/common/useInfiniteScroll";
import dustbin from "../../../../assets/icons/message-manager/dustbin.svg";
import { trackEvent } from "../../../../utils/analytics";

function SignageScheduledTable(props) {
  const {
    updateFlag,
    setUpdateFlag,
    onLoadScheduledTable,
    setNotificationProps,
    modalRef,
  } = props;
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState("nextRun");
  const [sortByAsc, setSortByAsc] = useState(false);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [scheduledData, setScheduledData] = useState([]);

  const tableRef = useRef();
  const tableWrapperRef = useRef();
  const hasTrackedRef = useRef(false);

  const loadScheduledTable = async (map, resetSelection = true) => {
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    try {
      const res = await API.getAllScheduledSignages();
      res.data.forEach((mess) => {
        const scheduled = commonMethods.formatScheduler(mess.scheduler);
        map.data[mess.id] = {
          id: mess.id,
          title: mess.title,
          scheduled,
          groups: Array.isArray(mess.groups)
            ? mess.groups.map((o) => o.name).join(", ")
            : mess.groups?.name || "",
          status: mess.status,
          createdBy: `${mess.createdBy?.firstName || ""} ${mess.createdBy?.lastName || ""}`,
          nextRun: mess.scheduler.timeNextRun
            ? commonMethods.formatDate(mess.scheduler.timeNextRun)
            : "",
          checked: false,
        };
      });
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setScheduledData(res.data);
      setDataCount(map.ids.length);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);

      // Track dashboard view once on initial load
      if (!hasTrackedRef.current && res.data.length > 0) {
        hasTrackedRef.current = true;
        const now = new Date();
        const scheduleTypes = { daily: 0, weekly: 0 };
        let totalHoursUntilNextRun = 0;
        let countWithNextRun = 0;

        res.data.forEach((item) => {
          if (item.scheduler?.type === "daily") scheduleTypes.daily++;
          else scheduleTypes.weekly++;

          if (item.scheduler?.timeNextRun) {
            const nextRun = new Date(item.scheduler.timeNextRun);
            const hoursUntil = (nextRun - now) / (1000 * 60 * 60);
            if (hoursUntil > 0) {
              totalHoursUntilNextRun += hoursUntil;
              countWithNextRun++;
            }
          }
        });

        trackEvent(
          "signage.dashboard_owner_scheduled",
          {
            scheduled_signage_count: res.data.length,
            schedule_type_daily_count: scheduleTypes.daily,
            schedule_type_weekly_count: scheduleTypes.weekly,
            avg_hours_until_next_run:
              countWithNextRun > 0
                ? Math.round(totalHoursUntilNextRun / countWithNextRun)
                : 0,
          },
          props.user,
        );
      }
    } catch (err) {
      // If it's error 502, skip error handling
      if (err.response && err.response.status === 502) return;
      setError(err.message);
      setDataLoaded(true);
    }
  };

  useEffect(() => {
    if (onLoadScheduledTable) {
      onLoadScheduledTable(() => loadScheduledTable(dataMap));
    }
  }, [onLoadScheduledTable]);

  const handleEditClick = (e, id) => {
    e.preventDefault();
    props.dispatch(
      signageRequested({
        id: id,
        current: 0,
        menuid: "signagePlusDashboardTab",
      }),
    );
    document.getElementById("signagePlusCreateSignageTab").click();
  };

  const dataMap = useMemo(() => {
    const map = {
      data: {},
      ids: [],
      selectedIds: {},
      filteredIds: [],
      sortedIds: [],
    };
    loadScheduledTable(map);
    return map;
  }, []);

  useEffect(() => {
    loadScheduledTable(dataMap);
  }, [updateFlag]);

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    rowHeight: 48,
  });

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== "") {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, scheduledData]);

  const handleSingleDelete = async (e, id) => {
    e.preventDefault();
    try {
      await API.deleteSignage(id);
      setSuccess(<FormattedMessage id="SIGNAGE_DELETED_SUCCESSFULLY" />);
      modalRef.current.hide();
      setUpdateFlag((prev) => !prev);
    } catch (error) {
      setError(
        <FormattedMessage
          id="SIGNAGE_DELETED_FAILED"
          values={{ errorMsg: error.message }}
        />,
      );
      modalRef.current.hide();
    }
  };

  const tableColumnArray = [
    {
      Header: "SIGNAGE",
      id: "title",
      accessor: "title",
      isVisible: true,
    },
    {
      Header: "SCHEDULED",
      id: "scheduled",
      accessor: "scheduled",
      isVisible: true,
    },
    {
      Header: "GROUPS",
      id: "groups",
      isVisible: true,
      accessor: "groups",
    },
    {
      Header: "CREATED_BY",
      id: "createdBy",
      isVisible: true,
      accessor: "createdBy",
    },
    {
      Header: "NEXT_RUN",
      id: "nextRun",
      accessor: "nextRun",
      isVisible: true,
    },
    {
      Header: "",
      id: "actions",
      isVisible: true,
      mWidth: "mWidth-125",
      Cell: ({ row }) => (
        <div className="actions">
          <button
            className="btn"
            id="editBtn"
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              if (handleEditClick) handleEditClick(e, row.original.id);
            }}
          >
            <img
              src={editIcon}
              className="actionIcon editAction"
              alt={<FormattedMessage id="EDIT" />}
            />
          </button>
          <span className="vertical-divider"></span>
          <button
            className="btn"
            id="deleteBtn"
            data-bs-toggle="modal"
            data-bs-target="#notificationModal"
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              setNotificationProps({
                icon: dustbin,
                message: "DELETE_SIGNAGE",
                description: "DELETE_SIGNAGE_DESCRIPTION",
                showSecondaryButton: true,
                primaryButtonText: "DELETE",
                onSubmit: (e) => handleSingleDelete(e, row.original.id),
              });
            }}
          >
            <img
              src={trashIcon}
              className="actionIcon deleteAction"
              alt={<FormattedMessage id="DELETE" />}
            />
          </button>
        </div>
      ),
    },
  ];

  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "sbds_owner_scheduled_signages_table",
    pageType: "SignageScheduled",
    modalId: "SignageScheduled",
    columns: tableColumnArray,
    data,
    setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount,
    setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success,
    setSuccess,
    error,
    setError,
    startIndex: startIndexForTable,
    selectedIds,
    setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    initialContainerHeight: "288px",
  };

  return (
    <>
      <MesTable {...tableProps} />
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(SignageScheduledTable);
